import { Component, OnInit, OnDestroy, ViewChild, NgZone, TemplateRef, ViewEncapsulation, signal} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Report } from '../../../models/report';
import { ReportingUtils } from 'src/app/utilities/reporting-utilities';
import { Server } from '../../../models/server';
import { DestinationEndpoint } from '../../../models/destination-endpoint';
import { ImportType } from '../../../models/import-type';
import { DataSourceType } from '../../../models/data-source-type';
import { IntegrationRunTime } from '../../../models/integration-runtime';
import { Integration } from '../../../models/integration';
import { ClientIntegrationService } from '../../../services/client-integration.service';
import { ToastService } from '../../../services/toast.service';
import { ReportService } from '../../../services/report.service';
import { ServerService } from 'src/app/services/server.service';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { LOSTalkerTitleService } from '../../../services/lostalker-title-service.service';
import { ColumnSettings } from 'src/app/models/column-settings';
import { BulkIntegrationActionType } from 'src/app/models/bulk-integration-action-type';
import { IntegrationRunTimeService } from 'src/app/services/integration-runtime.service';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { LTPngTableComponent } from 'src/app/features/grid/components/lt-png-table/lt-png-table.component';

const _flatten = filter => {
  const filters = filter.filters;
  if (filters) {
    return filters.reduce((acc, curr) => acc.concat(curr.filters ? _flatten(curr) : [curr]), []);
  }
  return [];
};

@Component({
  selector: 'app-integration-bulk-report',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './integration-bulk-report.component.html',
  styleUrls: ['./integration-bulk-report.component.scss'],
  standalone: false,
})
export class IntegrationBulkReportComponent implements OnInit, OnDestroy {
  pageLoading = true;
  public currReport: Report;
  @ViewChild('serverInfoDialog', { static: true }) serverInfoDialog: TemplateRef<any>;
  @ViewChild('integrationGrid') public integrationGrid: LTPngTableComponent<Integration>;

  public integrationGridSettings = signal<GridSettings>(null);
  public runtimeGridSettings = signal<GridSettings>(null);
  public gridLoading = signal<boolean>(true);

  componentDestroyed$ = new Subject<boolean>();
  reportingUtils: ReportingUtils;

  public bulkActionType: BulkIntegrationActionType = BulkIntegrationActionType.None;
  public bulkIntegActionType = BulkIntegrationActionType;
  bulkActionSelected = false;

  serverItems: Server[] = [];
  dataSourceTypeItems: DataSourceType[] = [];
  destinationEndpointItems: DestinationEndpoint[] = [];
  importTypeItems: ImportType[] = [];
  integrationRunTimeItems: IntegrationRunTime[] = [];

  serverItemsSelected: Server[] = [];
  dataSourceItemsSelected: DataSourceType[] = [];
  destinationEndpointItemsSelected: DestinationEndpoint[] = [];
  importTypeItemsSelected: ImportType[] = [];

  // Lock Unlock Report Specific
  public switchLocked = true;

  // Move Servers Report Specific
  selectedServer: Server = null;
  serverInfoReady = false;

  baseData: Integration[] = [];
  filteredData: Integration[] = [];
  gridData: Integration[] = [];
  public selectedIds: string[] = []; // selected integrations in grid

  exportFileName = 'IntegrationsReport.xlsx';

  // grid variables
  public gettingServerItems = true;
  
  serverInfoCols: ColumnSettings[] = [];
  runtimeCols: ColumnSettings[] = [];

  // RUNTIME REPORT VARS
  enableLastRunTime = true;
  enableNextRunTime = true;

  public lastDateFormControl = new FormControl<Date|null>(null, {nonNullable: false, validators: [Validators.required]});
  public nextDateFormControl = new FormControl<Date|null>(null, {nonNullable: false, validators: [Validators.required]});
  public importTypesControl = new FormControl<ImportType[]>([], {nonNullable: true, validators: [Validators.required]});
  public destinationsControl = new FormControl<DestinationEndpoint[]>([], {nonNullable: true, validators: [Validators.required]});

  public runTimeUpdateFormGroup = new FormGroup({
    lastDate: this.lastDateFormControl,
    nextDate: this.nextDateFormControl,
    destinationEndpoints: this.destinationsControl,
    importTypes: this.importTypesControl,
  });

  constructor(
    protected clientService: ClientIntegrationService,
    protected serverService: ServerService,
    protected reportService: ReportService,
    protected iRunTimeService: IntegrationRunTimeService,
    private titleService: LOSTalkerTitleService,
    protected toastService: ToastService,
    protected typeConstantService: TypeConstantService,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    private dialog: MatDialog,
  ) {
    this.pageLoading = true;
    this.reportingUtils = new ReportingUtils();
  }

  ngOnInit(): void {
    this.integrationGridSettings.set(this.setupGrid());
    this.serverInfoCols = this.reportingUtils.getDefaultColumnsByString('ServerInfo');
    this.runtimeCols = this.reportingUtils.getDefaultColumnsByString('IntegrationRunTime');

    this.route.params
    .pipe(takeUntil(this.componentDestroyed$))
    .subscribe((params) => {

      if (params['reportId'] == null) {
        // get report by url
        this.reportService.getReports()
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe((data) => {
          if (data != null) {
            const bulkReport = data.filter(x => x.routerUrl == '/' + this.route.snapshot.url[0].path + '/');
            if (bulkReport != null) {
              this.currReport = new Report(bulkReport[0]);
              this.exportFileName = this.currReport.exportFileName;
              this.titleService.setUpTitle('Report - ' + this.currReport.title);
            }
            else {
              this.toastService.toastCreate('Report failed to return', 'Danger');
              this.pageLoading = false;
            }
          }
          else {
            this.toastService.toastCreate('Reports failed to return', 'Danger');
            this.pageLoading = false;
          }
        });
      }
      else {
        // get report by id
        this.reportService.getReportById(params['reportId'])
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (data) => {
            if (data != null) {
              this.currReport = new Report(data);
              this.exportFileName = this.currReport.exportFileName;
              this.titleService.setUpTitle('Report - ' + data.title);
            }
            else {
              this.toastService.toastCreate('Report failed to return', 'Danger');
              this.pageLoading = false;
            }
          },
          error: (_error) => {
            this.toastService.toastCreate('Report failed to return', 'Danger');
            this.pageLoading = false;
          },
        });
      }
    });
    this.getFullServerInformation();
    this.getReportConstants();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      get data
     ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  // refresh grid for non Integration RunTime Bulk Action Types
  refreshSelection(): void {
    this.baseData = [];
    this.filteredData = [];
    this.gridData = [];
    this.selectedIds = [];
    this.gridLoading.set(true);
    this.getIntegrations();
  }

  // refresh grid for Integration RunTime Bulk Action Type
  refreshSelectionForRunTimes(): void {
    this.iRunTimeService.getAllIntegrationRunTimes()
    .pipe(takeUntil(this.componentDestroyed$))
    .subscribe(
      (data) => {
        this.integrationRunTimeItems = [];
        this.integrationRunTimeItems = data;

        if (this.baseData.length > 0) {
          if (this.integrationRunTimeItems.length > 0) {
            this.baseData.forEach(i => {
              i.integrationRunTimes = [];
              i.integrationRunTimes = this.integrationRunTimeItems.filter(x => x.integrationId == i.integrationId);
            });
          }
        }
      },
    );
  }

  // get integrations
  getIntegrations(): void {
    this.gridLoading.set(true);

    if (this.currReport.dataSproc !== '' && this.currReport.dataSproc !== undefined) {
      this.reportService.getIntegrationTypeQueryData(
        this.currReport.reportId)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (data) => {
            this.baseData = data;

            if (this.integrationRunTimeItems.length > 0 && this.baseData.length > 0) {
              this.baseData.forEach(i => {
                i.integrationRunTimes = this.integrationRunTimeItems.filter(x => x.integrationId == i.integrationId);
              });
            }

            this.updateFilteredData();
          },
          error: (_error) => {
            this.gridLoading.set(false);
          },
        });
    }
  }

  getReportConstants(): void {
    this.typeConstantService.getAllReportViewConstants()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (data) => {
          if (data != null) {
            this.dataSourceTypeItems = data.dataSourceTypes;
            if (this.dataSourceTypeItems.length > 0) {
              this.dataSourceTypeItems.sort((a, b) => a.dataSourceTypeId.localeCompare(b.dataSourceTypeId));
            }

            this.destinationEndpointItems = data.destinationEndpoints;
            if (this.destinationEndpointItems.length > 0) {
              this.destinationEndpointItems.sort((a, b) => a.endpointName.localeCompare(b.endpointName));
            }

            this.importTypeItems = data.importTypes;
            if (this.importTypeItems.length > 0) {
              this.importTypeItems.sort((a, b) => a.typeDescription.localeCompare(b.typeDescription));
            }

            this.integrationRunTimeItems = data.integrationRunTimes;

            if (this.baseData.length > 0) {
              if (this.integrationRunTimeItems.length > 0) {
                this.baseData.forEach(i => {
                  i.integrationRunTimes = this.integrationRunTimeItems.filter(x => x.integrationId == i.integrationId);
                });
              }
              this.updateFilteredData();
            }
          }
        },
        error: (_error) => {
          // console.log(error);
          this.toastService.toastCreate('Report Constants failed to return', 'Danger');
        },
    });
  }

  updateFilteredData(): void {
    this.filteredData = [];
    this.gridLoading.set(true);

    if (this.bulkActionSelected) {
      // bulk action selected so good to process
      this.filteredData = this.baseData;

      if (this.bulkActionType == BulkIntegrationActionType.LockUnlock) {
        this.filteredData = this.filteredData.filter(i => i.locked == !this.switchLocked);
      }

      // filter from cards
      if (this.currReport.hasFilterCards) {

        // RunTime V2
        if (this.integrationRunTimeItems.length > 0) {
          // Destination Endpoint
          if (this.destinationEndpointItemsSelected.length > 0
            && this.destinationEndpointItemsSelected.length != this.destinationEndpointItems.length) {
              const dests = this.destinationEndpointItemsSelected.map(i => i.destinationEndpointId);
              this.filteredData = this.filteredData.filter(i => i.integrationRunTimes != null);
              this.filteredData = this.filteredData.filter(x => x.integrationRunTimes?.filter(y => dests.some(z => y.destinationEndpointId == z)).length != 0);
          }

          // Import Type
          if (this.importTypeItemsSelected.length > 0
            && this.importTypeItemsSelected.length != this.importTypeItems.length) {
              const imTypes = this.importTypeItemsSelected.map(i => i.importTypeId);
              this.filteredData = this.filteredData.filter(i => i.integrationRunTimes != null);
              this.filteredData = this.filteredData.filter(x => x.integrationRunTimes?.filter(y => imTypes.some(z => y.importTypeId == z)).length != 0);
          }
        }
        
          // Data Source Types
        if(this.dataSourceItemsSelected != undefined && this.dataSourceItemsSelected.length > 0
          && this.dataSourceItemsSelected.length != this.dataSourceTypeItems?.length) {
            const dTypes = this.dataSourceItemsSelected.map(i => i.dataSourceTypeId);
            this.filteredData = this.filteredData.filter(x => dTypes.some(y => y == x.dataSourceTypeId));
        }

        // Server
        if (this.serverItemsSelected.length > 0) {
          const serverIds = this.serverItemsSelected.map(i => i.serverId);
          this.filteredData = this.filteredData.filter(i => serverIds.indexOf(i.serverId) != -1);
        }
      }

      this.integrationGridSettings.set(this.setupGrid());
    }
    
    this.gridLoading.set(false);
  }

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      BULK ACTION FUNCTIONS
     ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  onToggleActionChange(value: any): void {
    this.bulkActionSelected = true;
    if (value == 'LockUnlock') {
      this.bulkActionType = BulkIntegrationActionType.LockUnlock;
    }
    else if (value == 'MoveServerIntegrations') {
      this.bulkActionType = BulkIntegrationActionType.MoveServerIntegrations;
    }
    else if (value == 'UpdateRunTimes') {
      this.bulkActionType = BulkIntegrationActionType.UpdateRunTimes;

      this.enableLastRunTime = true;
      this.enableNextRunTime = true;
      this.lastDateFormControl.reset();
      this.nextDateFormControl.reset();
      this.importTypesControl.reset();
      this.destinationsControl.reset();
    }
    else {
      this.bulkActionSelected = false;
      // none match so default = None
      this.bulkActionType = BulkIntegrationActionType.None;
    }

    this.refreshSelection();
  }

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      MOVE SERVER REPORT FUNCTIONS
     ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  // MOVE SERVER - change server input dropdown
  changeServer(event: any): void {
    this.selectedServer = event.value;
  }

  moveIntegrations(): void {
    if (this.selectedIds.length > 0 && this.selectedServer != null && this.selectedServer != undefined) {

      const s = this.selectedIds.length.toString() + ' Selected Integrations to ' + this.selectedServer.serverId + '. Refreshing integrations.';

      this.clientService.moveServerIntegrations(this.selectedIds, this.selectedServer.serverId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.toastService.toastCreate('Moved ' + s, 'Success', {
            autoClose: false,
            keepAfterRouteChange: true,
          });
          this.refreshSelection();
        },
        error: (_error) => {
          // console.log(error);
          this.toastService.toastCreate('Error in Moving ' + s, 'Danger', {
            autoClose: false,
            keepAfterRouteChange: true,
          });
          this.refreshSelection();
        },
      });
    }
    else if (this.selectedServer == null || this.selectedServer == undefined) {
      this.toastService.toastCreate('Selected Server is null or empty. Select Server to move integrations to.', 'Info');
    }
    else {
      this.toastService.toastCreate('No integrations selected.', 'Info');
    }
  }

  getFullServerInformation(): void {
    this.gettingServerItems = true;
    this.serverService.getAllServersInformation()
    .pipe(takeUntil(this.componentDestroyed$))
    .subscribe((s) => {
      this.gettingServerItems = false;
      this.serverItems = s;
      if (this.serverItems.length > 0) {
        this.serverItems.sort((a, b) => a.description.localeCompare(b.description));
      }

      this.serverInfoReady = true;
    });
  }

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      LOCK UNLOCK REPORT FUNCTIONS
     ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  public onLockSwitchChange(): void {
    this.switchLocked = !this.switchLocked;
    this.updateFilteredData();
  }

  // LOCK UNLOCK REPORT - Action Button
  lockIntegrations(lockReason: string): void {
    if (this.selectedIds.length > 0) {
      let s = '' + (this.switchLocked ? ' Locked All Selected Integrations (' + lockReason + '): ' : ' Unlocked All Selected Integrations: ');
      s += this.selectedIds.length.toString() + ' Total Integrations. Refreshing integrations.';

      this.clientService.lockIntegrations(this.selectedIds, this.switchLocked, lockReason)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.toastService.toastCreate(s, 'Success', {
            autoClose: false,
            keepAfterRouteChange: true,
          });
          this.refreshSelection();
        },
        error: (_error) => {
          this.toastService.toastCreate('Error in ' + s, 'Danger', {
            autoClose: false,
            keepAfterRouteChange: true,
          });
          this.refreshSelection();
        },
      });
    }
    else {
      this.toastService.toastCreate('No integrations selected.', 'Info');
    }
  }

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      BUTTON FUNCTIONS
     ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  // select all button
  public selectAll(): void {
    let ids = this.baseData.map(i => i.integrationId);
    if (this.filteredData != null && this.filteredData.length > 0) {
      ids = this.filteredData.map(i => i.integrationId);
    }
    if (this.selectedIds.length > 0) {
      this.selectedIds = [];
    }
    this.selectedIds = ids;
  }

  // unselect all button
  public unSelectAll(): void {
    if (this.selectedIds.length > 0) {
       this.selectedIds = [];
    }
  }

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      Filter Card Functions
     ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  // reset filter cards
  public resetFilters(): void {
    this.dataSourceItemsSelected = [];
    this.destinationEndpointItemsSelected = [];
    this.serverItemsSelected = [];
    this.importTypeItemsSelected = [];
    this.updateFilteredData();
  }

  // apply filters changes
  public applyFilters(): void {
    this.updateFilteredData();
  }

  public externalFilterChange(): void {
    //console.log("externalFilterChange", value);
  }

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      grid functions
     ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  public originalOrder = (): number => {
    return 0;
  };

  public saveData(component: any): void {
    component.save();
  }

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      dialog functions
     ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  openServerInfoDialog() {
    this.dialog.open(this.serverInfoDialog);
  }

  public goBack(): void {
    this.location.back();
  }

  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      Integration RunTime Form Field Functions
     ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

  changeEnableRunTimeChecks(name: string, _x: boolean) {
    if (name == 'LAST') {
      this.enableLastRunTime = !this.enableLastRunTime;
      if (this.enableLastRunTime) {
        this.lastDateFormControl.enable();
      }
      else {
        this.lastDateFormControl.disable();
      }
    }
    else if (name == 'NEXT') {
      this.enableNextRunTime = !this.enableNextRunTime;
      if (this.enableNextRunTime) {
        this.nextDateFormControl.enable();
      }
      else {
        this.nextDateFormControl.disable();
      }
    }
  }

  public onRTSelectionRemoved(name: string, x: any) {
    if (name == 'DESTINATION') {
      const destEndpoints = this.destinationsControl.value as DestinationEndpoint[];
      this.removeFirst(destEndpoints, x);
      this.destinationsControl.setValue(destEndpoints);
    }
    else if (name == 'IMPORTTYPE') {
      const impTypes = this.importTypesControl.value as ImportType[];
      this.removeFirst(impTypes, x);
      this.importTypesControl.setValue(impTypes);
    }
  }

  private removeFirst(array: any[], toRemove: any): void {
    const index = array.indexOf(toRemove);
    if (index !== -1) {
      array.splice(index, 1);
    }
  }

  // UPDATE RUN TIMES - Action Button
  updateIntegrationRunTimes(): void {
    if (this.selectedIds.length > 0
        && this.destinationsControl.valid
        && this.importTypesControl.valid
        && (this.enableLastRunTime == true || this.enableNextRunTime == true)
        && (
          (this.enableLastRunTime == true && this.lastDateFormControl.valid
            && this.enableNextRunTime == true && this.nextDateFormControl.valid
            && this.lastDateFormControl.value <= this.nextDateFormControl.value)
          ||
          (
            (this.enableLastRunTime == true && this.lastDateFormControl.valid && this.enableNextRunTime == false)
            || (this.enableNextRunTime == true && this.nextDateFormControl.valid && this.enableLastRunTime == false)
          )
        )
        ) {

          const iIds = this.importTypesControl.value.map(x => x.importTypeId);
          const dIds = this.destinationsControl.value.map(x => x.destinationEndpointId);
          let lDate: Date = null;
          let nDate: Date = null;
          if (this.enableLastRunTime) {
            lDate = this.lastDateFormControl.value;
          }
          if (this.enableNextRunTime) {
            nDate = this.nextDateFormControl.value;
          }

          let msg = this.selectedIds.length.toString() + ' Selected Integrations.';
          this.iRunTimeService.updateBulkIntegrationRunTimes(this.selectedIds, iIds, dIds, lDate, nDate)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
              next: () => {
                this.toastService.toastCreate('Updated Integration Runtimes for ' + msg, 'Success', {
                  autoClose: false,
                  keepAfterRouteChange: true,
                });
                // this.refreshSelection();
                this.refreshSelectionForRunTimes();
              },
              error: (_error) => {
                if (_error instanceof HttpErrorResponse) {
                  if (_error.error != null && _error?.error?.toString().length > 0) {
                    msg = msg + ' ' + _error.error;
                  }
                }

                this.toastService.toastCreate('Error in Updating Integration Runtimes for ' + msg, 'Danger', {
                  autoClose: false,
                  keepAfterRouteChange: true,
                });
                // this.refreshSelection();
                this.refreshSelectionForRunTimes();
              },
            });
    }
    else if (this.enableLastRunTime == true && !this.lastDateFormControl.valid) {
      this.toastService.toastCreate('Last Run Time is enabled but not valid.', 'Warning');
    }
    else if (this.enableNextRunTime == true && !this.nextDateFormControl.valid) {
      this.toastService.toastCreate('Next Run Time is enabled but not valid.', 'Warning');
    }
    else if (this.enableLastRunTime == false && this.enableNextRunTime == false) {
      this.toastService.toastCreate('Last Run Time and Next Run Time are disabled. At least one is required.', 'Warning');
    }
    else if (!this.destinationsControl.valid) {
      this.toastService.toastCreate('Destination Endpoints to use to select Integration RunTimes by is not valid. At least one is required.', 'Warning');
    }
    else if (!this.importTypesControl.valid) {
      this.toastService.toastCreate('Import Types to use to select Integration RunTimes by is not valid. At least one is required.', 'Warning');
    }
    else if (this.enableLastRunTime == true && this.enableNextRunTime == true &&
      !(this.lastDateFormControl.value <= this.nextDateFormControl.value)) {
      this.toastService.toastCreate('Last Run Time must be before Next Run Time if both are enabled.', 'Warning');
    }
    else {
      this.toastService.toastCreate('No integrations selected.', 'Warning');
    }
  }
  
  setupGrid(): GridSettings {
    return new GridSettings({
      loading: this.gridLoading,
      dataKey: 'integrationId',
      data: this.filteredData,
      fixedGrid: true,
      columns: [
        new GridColumnData({ width: '270px', field: 'clientName', header: 'Client Name'}),
        new GridColumnData({ width: '300px', field: 'name', header: 'Integration Name'}),
        new GridColumnData({ width: '200px', field: 'dataSourceTypeId', header: 'DataSource'}),
        new GridColumnData({ width: '150px', field: 'serverId', header: 'Server'}),
        new GridColumnData({ width: '150px', field: 'locked', header: 'Locked'}),
        new GridColumnData({ width: '300px', field: 'lockReason', header: 'Lock Reason'}),
        new GridColumnData({ filterable: false, field: 'updatedDate', header: 'Updated Date', type: 'date', format: 'MM/dd/yyyy h:mm a'}),
        new GridColumnData({ width: '150px', field: 'ipRestricted', header: 'IP Restricted'}),
      ],
      sortMode: 'multiple',
      filter: {
        enabled: true,
        display: 'row',
        mode: 'row',
      },
      multisort: [
        {
          field: 'updatedDate',
          order: -1,
        },
      ],
      rowExpansion: {
        enabled: this.bulkActionType == BulkIntegrationActionType.UpdateRunTimes,
        allowMultiple: true,
      },
      selection: {
        enabled: true,
        showSelectAll: true,
        mode: 'multiple',
        selectionType: 'checkbox',
      },
    });
  }
  
  selectionChangeHandler(): void {
    this.selectedIds = this.integrationGrid.getSelectedItems().map(i => i.integrationId);
  }

}
